import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ArrowLeftIcon,
  AwardIcon,
  CreditCardIcon,
  LayersIcon
} from 'lucide-react';
import learningLevelsData from '../../data/learningLevels.json';
import api from '../../services/api';

const FlashCardsLevelStatistics = () => {
  const { t } = useTranslation();
  const { currentLanguage, isRTL } = useLanguage();
  const [flashCardsLevelId, setFlashCardsLevelId] = useState(1);
  const [flashCardsData, setFlashCardsData] = useState(null);
  const [flashCardsLoading, setFlashCardsLoading] = useState(false);

  const translateLevelName = (levelName) => {
    if (!levelName) return '';
    const translations = learningLevelsData[levelName] || {};
    return translations[currentLanguage] || levelName;
  };

  useEffect(() => {
    const fetchFlashCardsStats = async () => {
      setFlashCardsLoading(true);
      try {
        const res = await api.get(`/education/flash-cards/statistics/comprehensive`, {
          params: { levelId: flashCardsLevelId }
        });
        if (res.data.success) {
          setFlashCardsData(res.data.data);
        }
      } catch (err) {
        console.error('Error fetching flash cards statistics:', err);
      } finally {
        setFlashCardsLoading(false);
      }
    };
    fetchFlashCardsStats();
  }, [flashCardsLevelId]);

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors"
          >
            <ArrowLeftIcon className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
            {t('dashboard.stats.backToDashboard')}
          </Link>
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CreditCardIcon className="w-7 h-7 text-indigo-600" />
            {t('dashboard.stats.flashCardsLevelTitle')}
          </h1>
          <p className="text-slate-500 mt-1 text-sm">{t('dashboard.stats.flashCardsLevelSubtitle')}</p>
        </div>

        <section className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <CreditCardIcon className="w-5 h-5 text-indigo-600" />
              {t('dashboard.stats.flashCardsStats')}
            </h2>
            <div className="flex items-center gap-2">
              <LayersIcon className="w-4 h-4 text-slate-500" />
              <select
                value={flashCardsLevelId}
                onChange={(e) => setFlashCardsLevelId(Number(e.target.value))}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:border-indigo-300 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all"
              >
                {(flashCardsData?.availableLevels?.length
                  ? flashCardsData.availableLevels
                  : [{ id: flashCardsLevelId, name: '' }]
                ).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name ? (translateLevelName(l.name) || l.name) : t('dashboard.stats.selectLevel')}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="p-6">
            {flashCardsLoading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-2 border-indigo-500 border-t-transparent" />
              </div>
            ) : flashCardsData?.summary ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                  {[
                    { key: 'totalStudents', label: t('dashboard.stats.totalStudents'), value: flashCardsData.summary.totalStudents ?? 0, from: 'from-slate-500', to: 'to-slate-600' },
                    { key: 'activeStudents', label: t('dashboard.stats.activeStudents'), value: flashCardsData.summary.activeStudents ?? 0, from: 'from-emerald-500', to: 'to-emerald-600' },
                    { key: 'inactiveStudents', label: t('dashboard.stats.inactiveStudents'), value: flashCardsData.summary.inactiveStudents ?? 0, from: 'from-gray-500', to: 'to-gray-600' },
                    { key: 'totalFlashCardSessions', label: t('dashboard.stats.totalFlashCardSessions'), value: flashCardsData.summary.totalFlashCardSessions ?? 0, from: 'from-indigo-500', to: 'to-indigo-600' },
                    { key: 'correctFlashCardSessions', label: t('dashboard.stats.correctFlashCardSessions'), value: flashCardsData.summary.correctFlashCardSessions ?? 0, from: 'from-violet-500', to: 'to-violet-600' },
                    { key: 'correctPercentage', label: t('dashboard.stats.correctPercentage'), value: `${flashCardsData.summary.correctPercentage ?? 0}%`, from: 'from-amber-500', to: 'to-amber-600' }
                  ].map((item) => (
                    <div
                      key={item.key}
                      className={`rounded-xl bg-gradient-to-br ${item.from} ${item.to} p-4 text-white shadow-md`}
                    >
                      <p className="text-xs font-medium opacity-90 truncate">{item.label}</p>
                      <p className="text-2xl font-bold mt-1">{item.value}</p>
                    </div>
                  ))}
                </div>
                {flashCardsData.topStudents && flashCardsData.topStudents.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <AwardIcon className="w-4 h-4 text-amber-500" />
                      {t('dashboard.stats.topPerformers')}
                    </h3>
                    <div className="space-y-2">
                      {flashCardsData.topStudents.map((s) => (
                        <div
                          key={s.userId}
                          className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-slate-50 to-white hover:from-indigo-50 hover:to-white border border-slate-100 hover:border-indigo-200 transition-all"
                        >
                          <div
                            className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-white text-sm ${
                              s.rank === 1 ? 'bg-amber-500' :
                              s.rank === 2 ? 'bg-slate-400' :
                              s.rank === 3 ? 'bg-amber-700' : 'bg-slate-300'
                            }`}
                          >
                            {s.rank}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-900 truncate">
                              {s.firstName} {s.lastName}
                            </p>
                            <p className="text-xs text-slate-500">{s.studentNumber}</p>
                          </div>
                          <div className="text-end">
                            <p className="font-bold text-indigo-600">
                              {parseFloat(s.totalScore || 0).toFixed(1)}
                            </p>
                            <p className="text-xs text-slate-500">
                              {s.correctSessions}/{s.totalSessions} {t('dashboard.stats.correct')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-slate-500 text-center py-12">{t('dashboard.stats.noData')}</p>
            )}
          </div>
        </section>
    </div>
  );
};

export default FlashCardsLevelStatistics;
